import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { LocationFilter } from '@dmm/lib-react-ui-components';
import { getSuggestionsLocations } from '../../../../../requests/locationsRequest';
import { syncTranslationElements } from '../../../../../tppServices/translations/intlManager';
import {
  defaultSuggestions,
  fromBrowserLocation,
  getLocationTextField,
  locationTranslations,
  getPlaceIdToUse,
} from './filterSuggestionUtils';
import { useTPPServices } from '../../../../../tppServices/tppDIHooks';
import { nil } from '../../../../../utils/runOnceHelper';
import { getRadiuses } from '../../../utils/radiusSearch';
import { toBase64 } from '../../../../../tppServices/crossEnvHelpers';
import { fetchAndDetermineLocationText, updateDisplayValues } from './filterSuggestionUtils';
import { definedValue } from '../../../../../utils';
import { setLocationText} from '../../../../../store/actions';
import { useDispatch, useSelector } from 'react-redux';


const MIN_SEARCH_LENGTH = 3;


const FilterSuggestLocation = ({ context, urlLatLon = '', urlRadius = '', urlPlaceId = '', handleDataChange = nil, isFilterLocationSelected = false, shouldClearDisplay = false, clearOnlyRadius = false }) => {
  const [suggestedLocations, setSuggestedLocations] = useState(defaultSuggestions);
  const [radius, setRadius] = useState(() => {
    const options = getRadiuses();
    return {
      selected: 0,
      options
    };
  });
  const {languageService} = useTPPServices();
  const locale = languageService.getCurrentLocale();
  const locationRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const userRadiusRef = useRef('0'); // Store the user's radius selection
  const { formatMessage: t, messages } = syncTranslationElements();
  const dispatch = useDispatch();
  const locationTextFromRedux = useSelector(state => state?.app?.locationText || '');
  const searchParams = useSelector(state => state?.app?.params || {});
  const { translations, radiuses, myLocation } = useMemo(() => locationTranslations(t, messages, radius.options), [radius.options]);
  const fetchSuggestions = useCallback(async (searchQuery) => {
    const suggestions = await getSuggestionsLocations(searchQuery, locale, context);
    if (suggestions.error) {
      setSuggestedLocations(defaultSuggestions);
      return;
    }
    setSuggestedLocations(suggestions);
  }, [context, locale]);

  const handleInputChange = useCallback((event) => {
    const value = event.target.value;
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      if (value.length >= MIN_SEARCH_LENGTH) {
        fetchSuggestions(value);
      } else {
        setSuggestedLocations(defaultSuggestions);
      }
    }, 300);
  }, [fetchSuggestions, urlPlaceId, urlLatLon]);

  const onChangeRadius = (index) => {
    const selectedRadiusValue = definedValue(radius.options[index]?.value, '0');
    userRadiusRef.current = selectedRadiusValue;

    if (urlPlaceId) {
      const placeIdToUse = getPlaceIdToUse(locationRef, suggestedLocations, urlPlaceId);
      handleDataChange('location_place_id', { placeId: placeIdToUse, radius: selectedRadiusValue, latlon: null }, 'onChangeRadius_placeId');
    } else if (urlLatLon) {
      handleDataChange('coordinates', { latlon: urlLatLon, radius: selectedRadiusValue, placeId: null }, 'onChangeRadius_coords');
    } else {
      setRadius(prev => ({ ...prev, selected: index }));
      handleDataChange('radius', selectedRadiusValue, 'onChangeRadius_radius');

      /* istanbul ignore next */
      if (locationRef.current && typeof locationRef.current.setRadius === 'function') {
        const displayText = definedValue(radiuses[index], translations.any);
        locationRef.current.setRadius(displayText);
      }
    }
  };

  const getRadiusToUse = (radius) => {
    const userSelectedRadius = userRadiusRef.current;
    let radiusToUse;
    let radiusIndexToUse;
    if (userSelectedRadius === '0') {
      const radius100Index = radius.options.findIndex(option => option.value === '100');
      if (radius100Index !== -1) {
        radiusToUse = '100';
        radiusIndexToUse = radius100Index;
      } else {
        radiusToUse = '0';
        radiusIndexToUse = 0;
      }
    } else {
      radiusToUse = userSelectedRadius;
      radiusIndexToUse = radius.options.findIndex(option => option.value === userSelectedRadius);
    }

    return {radiusToUse, radiusIndexToUse};
  };

  const onChangeLocation = useCallback((event) => {
    const value = event?.target?.value || event;
    const currentBrowserLocation = fromBrowserLocation(event);
    const {radiusToUse, radiusIndexToUse } = getRadiusToUse(radius);

    if (currentBrowserLocation) {
      locationRef.current.setTypeaheadSearch(myLocation.name);
      // For browser location, use the currently selected radius
      const coordsB64 = toBase64(JSON.stringify([currentBrowserLocation.lat, currentBrowserLocation.lon]));
      handleDataChange('coordinates', { latlon: coordsB64, radius: radiusToUse, placeId: null }, 'onChangeLocation_myLocation');
    }

    const locationChoice = suggestedLocations?.locationIds.find(item => item?.name === value);
    if (locationChoice) {
      dispatch(setLocationText(value));
      locationRef.current.setTypeaheadSearch(value);
      handleDataChange('location_place_id', {
        placeId: locationChoice.id,
        radius: radiusToUse,
        latlon: null
      }, 'onChangeLocation_placeId');
    }

    const displayText = definedValue(radiuses[radiusIndexToUse], translations.any);
    locationRef?.current?.setRadius(displayText);
    // Set the correct radius to use in the dropdown
    setRadius(prev => ({ ...prev, selected: radiusIndexToUse }));
  }, [suggestedLocations, radius.options, radius.selected, handleDataChange, myLocation, radiuses, translations]);

  useEffect(() => {
    const findAndWatchInput = () => {
      const inputElement = getLocationTextField();
      inputRef.current = inputElement;
      inputElement?.addEventListener('input', handleInputChange);
    };

    findAndWatchInput();

    return () => {
      /* istanbul ignore next */
      if (inputRef.current) {
        inputRef.current.removeEventListener('input', handleInputChange);
      }
      /* istanbul ignore if */
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [handleInputChange]);

  useEffect(() => {
    const syncDisplay = async () => {
      const radiusOrLatLon = urlLatLon || urlRadius;
      const locationText = await fetchAndDetermineLocationText(
        urlPlaceId,
        radiusOrLatLon,
        context,
        myLocation.name,
        suggestedLocations,
        locale,
        isFilterLocationSelected,
      );
      const effectiveRadiusIndex = updateDisplayValues(
        locationText,
        urlPlaceId,
        urlRadius,
        radius.options,
        isFilterLocationSelected,
        translations,
        radiuses,
        locationRef
      );
      dispatch(setLocationText(locationText));
      setRadius(prev => ({ ...prev, selected: effectiveRadiusIndex }));
    };
    syncDisplay();
  }, [urlPlaceId, urlLatLon, urlRadius, context, myLocation.name, radius.options, translations, radiuses]);


  useEffect(() => {
    // When filters are cleared, all these values will be empty/false
    if (!urlPlaceId && !urlLatLon && !isFilterLocationSelected) {

      userRadiusRef.current = '0';
    }

    if (isFilterLocationSelected) {
      locationRef.current.setRadius(translations.any);
      locationRef.current.setTypeaheadSearch('');
    }
  }, [urlPlaceId, urlLatLon, isFilterLocationSelected]);


  useEffect(() => {
    if (shouldClearDisplay) {
      if (clearOnlyRadius) {
        locationRef.current.setRadius(translations.any);
        userRadiusRef.current = '0';
      } else {
        locationRef.current.setTypeaheadSearch('');
        locationRef.current.setRadius(translations.any);
        userRadiusRef.current = '0';
        dispatch(setLocationText(''));
      }
    }
  }, [shouldClearDisplay, clearOnlyRadius]);

  useEffect(() => {
    if (locationRef.current && !shouldClearDisplay) {
      const currentRadiusValue = radius.options[radius.selected]?.value || '0';
      const hasLatLon = Boolean(urlLatLon) || Boolean(searchParams.latlon);
      const hasPlaceId = Boolean(urlPlaceId) || Boolean(searchParams.placeId);

      if (currentRadiusValue === '0' && !hasLatLon && !hasPlaceId) {
        locationRef.current.setTypeaheadSearch('');
        return;
      }

      if (locationTextFromRedux) {
        const currentValue = locationRef.current.getTypeaheadSearch();
        if (currentValue === '' && currentValue !== locationTextFromRedux) {
          locationRef.current.setTypeaheadSearch(locationTextFromRedux);
        }
      }
    }
  }, [locationTextFromRedux, shouldClearDisplay, radius.selected, radius.options, urlLatLon, urlPlaceId, searchParams.latlon, searchParams.placeId]);

  return (
    <LocationFilter
      heading={translations.location}
      radiusDefaultText={''}
      radiusLabel={translations.radius}
      radiusOptions={radiuses}
      onChangeRadius={onChangeRadius}
      currentLocationText={translations.locationCurrrent}
      locationDeniedAlertText={translations.locationDisabled}
      onLocationChange={onChangeLocation}
      searchNotFoundText={translations.notFound}
      searchOptions={suggestedLocations.names}
      searchPlaceholder={translations.locationPlaceholder}
      ref={locationRef}
      minCharacters={MIN_SEARCH_LENGTH}
    />
  );
};

FilterSuggestLocation.propTypes = {
  context: PropTypes.object.isRequired,
  isFilterLocationSelected: PropTypes.bool,
  handleDataChange: PropTypes.func,
  shouldClearDisplay: PropTypes.bool,
  clearOnlyRadius: PropTypes.bool,
  urlRadius: PropTypes.string,
  urlLatLon: PropTypes.string,
  urlPlaceId: PropTypes.string
};

export default React.memo(FilterSuggestLocation);
